import { writable } from 'svelte/store'
import { nanoid } from 'nanoid'

const Element = (setter, initial, formatter = x => x) => {
  let listening = true
  let initialValue
  let committedValue
  let formattedValue

  const handler = {}

  let unformatter = x => x

  const store = writable({})
  const { subscribe, update } = store

  const $ = {}

  $.id = nanoid()

  $.init = ({ value, disabled, ...props }) => {
    if (listening) {
      initialValue = value
      committedValue = JSON.stringify(value)
      // console.log('format', { value, formatter, formattedValue })
      formattedValue = formatter(value)
      // console.log('initValue is listening!', { listening, value, disabled, ...props, formattedValue })
      update(state => ({
        ...state,
        ...props,
        disabled: !!disabled,
        value,
        formatted: formattedValue,
        error: undefined
      }))
    }
    listening = true
  }

  const merge = (state, obj) => {
    // console.log(JSON.stringify({ formatted: obj.formatted, formattedValue }))
    if (obj.formatted !== formattedValue) {
      const newValue = unformatter(obj.formatted)
      if (newValue) {
        // console.log(`formatted value has changed from ${formattedValue} to ${obj.formatted} (reverse=${newValue})`)
        obj.value = newValue
        listening = false
        setter(obj.value)
        committedValue = JSON.stringify(obj.value)
        formattedValue = formatter(obj.value) // reformat back ?
        return { ...state, ...obj }
      }
    }
    // console.log(JSON.stringify({ value: obj.value, committedValue  }))
    if (JSON.stringify(obj.value) !== committedValue) {
      listening = false
      setter(obj.value)
      committedValue = JSON.stringify(obj.value)
      formattedValue = formatter(obj.value)
      return { ...state, ...obj, formatted: formattedValue }
    }
    return { ...state, ...obj }
  }

  $.subscribe = subscribe

  // used when bind on <input>
  $.set = obj => {
    // console.log('$.set', obj.value)
    update(state => merge(state, obj))
  }

  $.setValue = value => $.set({ value })

  $.updateValue = updater => {
    update(state => {
      const value = updater(state.value)
      return merge(state, { value })
    })
  }

  $.setFormatter = (fn1, fn2) => {
    if (fn1) formatter = fn1
    if (fn2) unformatter = fn2
  }

  $.getInitialValue = () => initialValue

  $.reset = () => {
    $.set({ value: initialValue })
  }

  $.setHandler = (type, h) => {
    handler[type] = h
  }

  $.onEvent = (event, ...args) => {
    // set show password value globably
    if (args[0] === 'show-password') {
      update(state => {
        const tempObj = { ...state }
        tempObj.showPassword = args[1]
        return tempObj
      })
    }

    if (handler[event.type]) {
      handler[event.type](event, ...args)
    }
  }

  $.validateValue = async () => {
    // tmporary solution
    let internal
    update(state => {
      internal = state
      return state
    })
    if (internal.disabled) return true
    if (
      internal.required &&
      (!internal.value ||
        (typeof internal.validateRequired === 'function' && !internal.validateRequired(internal.value)))
    ) {
      update(state => ({ ...state, error: internal.required }))
      return false
    }
    try {
      const [success, error, info] = internal.validate ? await internal.validate(internal.value) : [true]
      update(state => ({ ...state, error: success ? undefined : error, info: info || state.info }))
      return success
    } catch (e) {
      update(state => ({ ...state, error: undefined }))
      return true
    }
  }

  $.selfDestroy = () => {
    $.reset()
    if (handler.onDestroy) handler.onDestroy()
  }

  $.setValidationMode = mode => {
    // reset
    $.setHandler('blur', undefined)
    $.setHandler('change', undefined)
    $.setHandler('input', undefined)

    switch (mode) {
      case 'aggressive':
        $.setHandler('change', $.validateValue)
        $.setHandler('input', $.validateValue)
        break
      case 'lazy':
        $.setHandler('blur', () => {
          $.validateValue()

          $.setHandler('blur', undefined)
          $.setHandler('input', $.validateValue)
        })
        $.setHandler('change', $.validateValue)
        break
      case 'eager':
        $.setHandler('blur', async () => {
          if (!(await $.validateValue())) {
            $.setHandler('input', async () => {
              if (await $.validateValue()) {
                $.setHandler('input', undefined)
              }
            })
          }
        })
        $.setHandler('change', $.validateValue)
        break
    }
  }

  if (initial) $.init(initial)

  return Object.freeze($)
}

export default Element
